<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleOverlayClick">
    <div class="dialog-container" @click.stop>
      <div class="dialog-header">
        <h2 class="dialog-title">Delete Group</h2>
        <button @click="cancel" class="close-button" aria-label="Close">
          <span>×</span>
        </button>
      </div>
      
      <div class="dialog-body">
        <div class="warning-icon">
          <i class="icon-warning"></i>
        </div>
        <p class="dialog-message">
          Are you sure you want to delete "<strong>{{ groupName }}</strong>"?
        </p>
        <p class="dialog-submessage">
          This action cannot be undone. All group data will be permanently removed.
        </p>
      </div>
      
      <div class="dialog-footer">
        <button 
          @click="cancel" 
          class="btn btn-secondary"
          :disabled="isDeleting"
        >
          Cancel
        </button>
        <button 
          @click="confirmDelete" 
          class="btn btn-danger"
          :disabled="isDeleting"
        >
          <span v-if="isDeleting" class="spinner"></span>
          {{ isDeleting ? 'Deleting...' : 'Delete Group' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

defineProps<{
  isOpen: boolean;
  groupName: string;
  groupId: number;
}>();

const emit = defineEmits<{
  confirm: [];
  cancel: [];
  close: [];
}>();

const isDeleting = ref(false);

const handleOverlayClick = () => {
  emit('close');
};

const confirmDelete = async () => {
  isDeleting.value = true;
  emit('confirm');
};

const cancel = () => {
  if (!isDeleting.value) {
    emit('cancel');
    emit('close');
  }
};

// Reset state when dialog closes
defineExpose({
  resetState: () => {
    isDeleting.value = false;
  }
});
</script>

<style scoped>
@import '../../assets/main.css';

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.dialog-container {
  background: white;
  border-radius: var(--radius-xl);
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-6);
  border-bottom: 1px solid rgb(229 231 235);
}

.dialog-title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-dark-gray);
}

.close-button {
  background: none;
  border: none;
  font-size: var(--font-size-2xl);
  color: rgb(156 163 175);
  cursor: pointer;
  padding: 0;
  width: var(--spacing-8);
  height: var(--spacing-8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  transition: all 0.2s ease;
}

.close-button:hover {
  background: rgb(249 250 251);
  color: rgb(107 114 128);
}

.dialog-body {
  padding: var(--spacing-6);
  text-align: center;
}

.warning-icon {
  margin-bottom: var(--spacing-4);
}

.icon-warning::before {
  content: '⚠️';
  font-size: 3rem;
  display: block;
}

.dialog-message {
  font-size: var(--font-size-base);
  color: var(--color-neutral-dark-gray);
  margin-bottom: var(--spacing-2);
}

.dialog-message strong {
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-black);
}

.dialog-submessage {
  font-size: var(--font-size-sm);
  color: rgb(107 114 128);
  margin: 0;
}

.dialog-footer {
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  padding: var(--spacing-6);
  border-top: 1px solid rgb(229 231 235);
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: var(--spacing-2);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .dialog-container {
    width: 95%;
    margin: var(--spacing-4);
  }
  
  .dialog-header,
  .dialog-body,
  .dialog-footer {
    padding: var(--spacing-4);
  }
  
  .dialog-footer {
    flex-direction: column-reverse;
  }
  
  .dialog-footer .btn {
    width: 100%;
  }
}
</style>
